import { useState, useEffect, useCallback } from "react";
import { toast, Toaster } from "sonner";
import { DayPicker } from "react-day-picker";
import {
  Loader2,
  LogOut,
  Trash2,
  Plus,
  Users,
  CalendarDays,
  MapPin,
  Send,
  Clock,
} from "lucide-react";
import {
  api,
  authHeaders,
  getToken,
  setToken,
  clearToken,
  formatApiErrorDetail,
  formatLongDate,
  formatShortDate,
  timeRange,
  toISODate,
} from "../lib/api";
import { BRAND } from "../data/brand";

const LOCATIONS = [
  "Anytime Fitness — South Street",
  "Anytime Fitness — Hollis Street",
];

const CAP_BY_LOCATION = (loc) => (loc && loc.toLowerCase().includes("hollis") ? 4 : 6);

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      toast.success("Welcome back, Coach.");
      onSuccess();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm border border-white/10 bg-[#111111] p-10"
      >
        <img src={BRAND.logo} alt="logo" className="h-12 w-12 object-contain mb-6" />
        <h1 className="font-display font-bold text-2xl uppercase tracking-tight mb-1">
          Admin Access
        </h1>
        <p className="text-neutral-500 text-sm mb-8">Train With Tristian dashboard</p>
        <input
          data-testid="admin-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="w-full bg-transparent border-b border-white/20 py-3 mb-6 placeholder:text-neutral-600 focus:outline-none focus:border-[#d4af37] transition-colors"
        />
        <input
          data-testid="admin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full bg-transparent border-b border-white/20 py-3 mb-8 placeholder:text-neutral-600 focus:outline-none focus:border-[#d4af37] transition-colors"
        />
        <button
          data-testid="admin-login-btn"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-[#d4af37] text-black py-4 font-bold text-sm tracking-[0.12em] uppercase hover:bg-[#f3e5ab] transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function ProgramBuilder({ onCreated }) {
  const [location, setLocation] = useState(LOCATIONS[0]);
  const [title, setTitle] = useState("");
  const [startTime, setStartTime] = useState("05:00");
  const [endTime, setEndTime] = useState("06:00");
  const [days, setDays] = useState([]); // Date[]
  const [creating, setCreating] = useState(false);

  const submit = async () => {
    if (days.length < 1) {
      toast.error("Select at least one session date on the calendar.");
      return;
    }
    if (!startTime || !endTime) {
      toast.error("Set a start and end time.");
      return;
    }
    if (startTime >= endTime) {
      toast.error("End time must be after the start time.");
      return;
    }
    setCreating(true);
    try {
      const sessions = days.map((d) => toISODate(d)).sort();
      await api.post(
        "/admin/programs",
        {
          location,
          title,
          start_time: startTime,
          end_time: endTime,
          sessions,
        },
        { headers: authHeaders() }
      );
      toast.success(`Boot camp published with ${sessions.length} sessions.`);
      setDays([]);
      setTitle("");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  return (
    <section className="border border-white/10 bg-[#111111] p-6 sm:p-8 mb-12">
      <h2 className="font-display font-bold text-xl uppercase tracking-tight mb-6 flex items-center gap-2">
        <Plus className="w-5 h-5 text-[#d4af37]" /> Build a New Boot Camp
      </h2>

      <div className="grid lg:grid-cols-2 gap-8" data-testid="create-cohort-form">
        {/* Left: details */}
        <div className="space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
              Location
            </label>
            <select
              data-testid="cohort-location-select"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-white/15 py-3 px-3 text-sm focus:outline-none focus:border-[#d4af37] transition-colors"
            >
              {LOCATIONS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
            <p className="text-xs text-neutral-500 mt-2">
              Capacity for this location: <span className="text-[#d4af37] font-semibold">{CAP_BY_LOCATION(location)} people</span>
            </p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
              Label (optional)
            </label>
            <input
              data-testid="cohort-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Morning Momentum"
              className="w-full bg-[#0a0a0a] border border-white/15 py-3 px-3 text-sm focus:outline-none focus:border-[#d4af37] transition-colors placeholder:text-neutral-600"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
                Start time
              </label>
              <input
                data-testid="cohort-start-time"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-white/15 py-3 px-3 text-sm focus:outline-none focus:border-[#d4af37] transition-colors [color-scheme:dark]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
                End time
              </label>
              <input
                data-testid="cohort-end-time"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-white/15 py-3 px-3 text-sm focus:outline-none focus:border-[#d4af37] transition-colors [color-scheme:dark]"
              />
            </div>
          </div>

          <div className="border border-white/10 bg-[#0a0a0a] p-4">
            <p className="text-sm text-neutral-300 mb-1">
              <span className="text-[#d4af37] font-bold text-lg">{days.length}</span> session
              {days.length === 1 ? "" : "s"} selected
              {days.length !== 15 && (
                <span className="text-neutral-500"> (target: 15)</span>
              )}
            </p>
            {days.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {[...days]
                  .sort((a, b) => a - b)
                  .map((d) => (
                    <span
                      key={toISODate(d)}
                      className="text-xs bg-[#d4af37]/15 text-[#d4af37] px-2 py-1 rounded"
                    >
                      {formatShortDate(toISODate(d))}
                    </span>
                  ))}
              </div>
            )}
          </div>

          <button
            data-testid="create-cohort-btn"
            onClick={submit}
            disabled={creating}
            className="w-full bg-[#d4af37] text-black px-6 py-3 font-bold text-sm uppercase tracking-wide hover:bg-[#f3e5ab] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publish Boot Camp"}
          </button>
        </div>

        {/* Right: calendar multi-select */}
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
            Pick the session days (non-consecutive OK)
          </label>
          <div
            data-testid="builder-calendar"
            className="inline-block border border-white/10 bg-[#0a0a0a] p-3 rounded-md twt-calendar"
          >
            <DayPicker
              mode="multiple"
              selected={days}
              onSelect={(d) => setDays(d || [])}
              disabled={{ before: new Date() }}
              numberOfMonths={1}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Dashboard({ onLogout }) {
  const [programs, setPrograms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [p, b] = await Promise.all([
        api.get("/admin/programs", { headers: authHeaders() }),
        api.get("/admin/bookings", { headers: authHeaders() }),
      ]);
      setPrograms(p.data);
      setBookings(b.data);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error("Could not load data.");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const removeProgram = async (id) => {
    if (!window.confirm("Delete this boot camp and all its bookings?")) return;
    try {
      await api.delete(`/admin/programs/${id}`, { headers: authHeaders() });
      toast.success("Boot camp deleted.");
      load();
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancel ${b.name}'s booking?`)) return;
    try {
      const { data } = await api.delete(`/admin/bookings/${b.id}`, { headers: authHeaders() });
      toast.success(
        data.promoted
          ? `Booking cancelled. ${data.promoted.name} was promoted from the waitlist.`
          : "Booking cancelled."
      );
      load();
    } catch (err) {
      toast.error("Could not cancel booking.");
    }
  };

  const sendReminders = async () => {
    try {
      const { data } = await api.post("/admin/send-reminders", {}, { headers: authHeaders() });
      toast.success(`Reminders processed. ${data.sent ?? 0} sent.`);
    } catch (err) {
      toast.error("Could not send reminders.");
    }
  };

  const waitlistCount = (pid) =>
    bookings.filter((b) => b.program_id === pid && b.status === "waitlist").length;

  return (
    <div className="min-h-screen px-5 sm:px-8 py-10 max-w-[1200px] mx-auto">
      <header className="flex items-center justify-between mb-12">
        <div className="flex items-center gap-3">
          <img src={BRAND.logo} alt="logo" className="h-10 w-10 object-contain" />
          <div>
            <p className="font-display font-bold uppercase tracking-tight leading-none">
              Coach Dashboard
            </p>
            <p className="text-xs text-neutral-500 mt-1">Train With Tristian</p>
          </div>
        </div>
        <button
          data-testid="admin-logout-btn"
          onClick={onLogout}
          className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white border border-white/15 px-4 py-2 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </header>

      <ProgramBuilder onCreated={load} />

      {/* Programs */}
      <section className="mb-14">
        <h2 className="font-display font-bold text-xl uppercase tracking-tight mb-6 flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#d4af37]" /> Boot Camps & Availability
        </h2>
        {loading ? (
          <div className="text-neutral-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : programs.length === 0 ? (
          <p className="text-neutral-500">No boot camps yet. Build your first one above.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="admin-cohort-list">
            {programs.map((p) => (
              <div key={p.id} className="border border-white/10 bg-[#111111] p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="flex items-center gap-2 text-sm text-neutral-400 mb-1">
                      <MapPin className="w-4 h-4 text-[#d4af37]" /> {p.location}
                    </p>
                    <p className="font-display font-bold text-lg flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#d4af37]" />
                      {timeRange(p.start_time, p.end_time)}
                    </p>
                    {p.title ? (
                      <p className="text-sm text-neutral-500">{p.title}</p>
                    ) : null}
                  </div>
                  <button
                    data-testid={`delete-cohort-${p.id}`}
                    onClick={() => removeProgram(p.id)}
                    className="text-neutral-500 hover:text-red-400 transition-colors"
                    title="Delete boot camp"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-neutral-400 mt-3">
                  {p.session_count} sessions · starts{" "}
                  {p.start_date ? formatLongDate(p.start_date) : "—"}
                </p>
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <span
                    className={`text-xs font-bold px-2 py-1 ${
                      p.is_full
                        ? "bg-amber-500/15 text-amber-400"
                        : "bg-[#d4af37]/15 text-[#d4af37]"
                    }`}
                  >
                    {p.is_full ? "FULL" : `${p.spots_remaining}/${p.capacity} SPOTS LEFT`}
                  </span>
                  <span className="text-xs text-neutral-500 flex items-center gap-1">
                    <Users className="w-3 h-3" /> {p.confirmed_count} confirmed ·{" "}
                    {waitlistCount(p.id)} waitlist
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Bookings */}
      <section>
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h2 className="font-display font-bold text-xl uppercase tracking-tight flex items-center gap-2">
            <Users className="w-5 h-5 text-[#d4af37]" /> Applicants ({bookings.length})
          </h2>
          <button
            data-testid="send-reminders-btn"
            onClick={sendReminders}
            className="flex items-center gap-2 text-xs uppercase tracking-wide border border-white/15 text-neutral-300 px-4 py-2 hover:border-[#d4af37] hover:text-white transition-colors"
          >
            <Send className="w-4 h-4 text-[#d4af37]" /> Send reminders now
          </button>
        </div>
        {bookings.length === 0 ? (
          <p className="text-neutral-500">No applications yet.</p>
        ) : (
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full text-sm" data-testid="admin-bookings-table">
              <thead className="bg-[#111111] text-neutral-400 uppercase text-xs tracking-wider">
                <tr>
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4">Contact</th>
                  <th className="text-left p-4">Boot Camp</th>
                  <th className="text-left p-4">Goal</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-left p-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-white/5 align-top">
                    <td className="p-4 font-medium">{b.name}</td>
                    <td className="p-4 text-neutral-400">
                      <div>{b.email}</div>
                      <div>{b.phone}</div>
                    </td>
                    <td className="p-4 text-neutral-400">
                      <div>{b.location}</div>
                      <div className="text-xs">
                        {b.time_range}
                        {b.start_date ? ` · starts ${formatShortDate(b.start_date)}` : ""}
                      </div>
                    </td>
                    <td className="p-4 text-neutral-400 max-w-xs">{b.goal}</td>
                    <td className="p-4">
                      <span
                        className={`text-xs font-bold px-2 py-1 ${
                          b.status === "confirmed"
                            ? "bg-[#d4af37]/15 text-[#d4af37]"
                            : "bg-amber-500/15 text-amber-400"
                        }`}
                      >
                        {b.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4">
                      <button
                        data-testid={`cancel-booking-${b.id}`}
                        onClick={() => cancelBooking(b)}
                        className="text-neutral-500 hover:text-red-400 transition-colors"
                        title="Cancel booking"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);

  const check = useCallback(async () => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    try {
      await api.get("/auth/me", { headers: authHeaders() });
      setAuthed(true);
    } catch {
      clearToken();
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="noise-overlay" />
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: "#111111",
            border: "1px solid rgba(212,175,55,0.4)",
            color: "#fff",
          },
        }}
      />
      {authed === null ? (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#d4af37]" />
        </div>
      ) : authed ? (
        <Dashboard onLogout={logout} />
      ) : (
        <Login onSuccess={() => setAuthed(true)} />
      )}
    </div>
  );
}
